import React, { useState } from 'react';
import { Shield, Trash2, AlertTriangle, CheckCircle, Mail, Clock, UserX, Database, FileText } from 'lucide-react';

export default function DeleteAccount() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (confirmText.toLowerCase() !== 'delete my account') {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    // In production, this would send to your backend
    // For now, we'll just show success and they can email support
    setSubmitted(true);
    setError('');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-blue-600 p-1.5 rounded-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">OilWellX</span>
            </div>
            <a 
              href="/"
              className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
            >
              Back to App
            </a>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Deletion Request Submitted
            </h1>
            <p className="text-gray-600 mb-6">
              Your account deletion request has been received. Our team will process your request within 
              <strong> 7 business days</strong>. You will receive a confirmation email at <strong>{email}</strong> 
              once your account and data have been permanently deleted.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">What happens next?</p>
                  <ul className="mt-2 text-sm text-amber-700 space-y-1">
                    <li>• You will receive an email confirmation within 24 hours</li>
                    <li>• Your data will be deleted within 7 business days</li>
                    <li>• You will lose access to your account immediately upon deletion</li>
                    <li>• This action cannot be undone</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-sm text-gray-500">
                Questions? Contact us at{' '}
                <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:underline">
                  support@oilwellx.com
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">OilWellX</span>
          </div>
          <a 
            href="/"
            className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
          >
            Back to App
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserX className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mb-4">
            Delete Your Account
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Request permanent deletion of your OilWellX or NavigatorX account and all associated data.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Info Section */}
          <div className="space-y-6">
            {/* What Gets Deleted */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="h-5 w-5 text-red-600 mr-2" />
                What Gets Deleted
              </h2>
              <ul className="space-y-3">
                {[
                  "Your account profile and login credentials",
                  "Production data and well entries you've created",
                  "Uploaded photos and documents",
                  "Location history and route data",
                  "Voice command history (if enabled)",
                  "Subscription and payment history",
                  "All app preferences and settings"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <Trash2 className="h-4 w-4 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 text-blue-600 mr-2" />
                Deletion Timeline
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Request Submitted</p>
                    <p className="text-sm text-gray-500">Immediate confirmation email</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Processing Period</p>
                    <p className="text-sm text-gray-500">Up to 7 business days</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Deletion Complete</p>
                    <p className="text-sm text-gray-500">Final confirmation email sent</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alternative Contact */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Prefer Email?
              </h3>
              <p className="text-blue-800 text-sm mb-3">
                You can also request account deletion by emailing us directly:
              </p>
              <a 
                href="mailto:privacy@oilwellx.com?subject=Account%20Deletion%20Request"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Mail className="h-4 w-4 mr-2" />
                privacy@oilwellx.com
              </a>
            </div>
          </div>

          {/* Form Section */}
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="h-5 w-5 text-gray-600 mr-2" />
                Deletion Request Form
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Account Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the email associated with your OilWellX or NavigatorX account
                  </p>
                </div>

                {/* Reason (Optional) */}
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Leaving (Optional)
                  </label>
                  <select
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="">Select a reason...</option>
                    <option value="no-longer-needed">No longer need the service</option>
                    <option value="switching">Switching to different software</option>
                    <option value="too-expensive">Too expensive</option>
                    <option value="privacy">Privacy concerns</option>
                    <option value="difficult-to-use">Difficult to use</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Warning Box */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        This action is permanent and cannot be undone
                      </p>
                      <p className="mt-1 text-sm text-red-700">
                        All your data will be permanently deleted and you will lose access to your account.
                        If you're part of a company account, your personal data will be removed but 
                        company operational data may be retained per your organization's policy.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Confirmation */}
                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                    Type "DELETE MY ACCOUNT" to confirm *
                  </label>
                  <input
                    type="text"
                    id="confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors font-mono"
                    required
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full px-6 py-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-5 w-5" />
                  Request Account Deletion
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you acknowledge that your account and all associated data 
                  will be permanently deleted within 7 business days.
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
            <span className="text-gray-300">|</span>
            <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
            <span className="text-gray-300">|</span>
            <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:underline">Contact Support</a>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            © 2025-2026 TGE LLC. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
}
